import { BASE_DOMAIN } from '@shared/constants/config';

// Logging out of the portal revokes only the portal's own Keycloak tokens: the realm session stays
// alive for NumberCruncher Web, which shares it. While that session lives, `check-sso` on the next
// page load (and any `login()`) would sign the user straight back in without a password. This marker
// records "the user logged out of the portal" so the bootstrap skips `check-sso` and every login asks
// for credentials again (`prompt=login`). It is cleared once a real sign-in completes.
//
// A cookie rather than localStorage: logout lands on the MAIN portal host, while the user may have
// logged out from a company subdomain, and localStorage is per origin. Scoped to the base domain so
// the main host and every tenant subdomain see the same value.

export const COOKIE_NAME = 'cp_logged_out';
const MARKED_VALUE = '1';

/** `; Domain=.<base>` when the current host lives under the configured base domain, else ''. */
function domainAttribute() {
  const host = (globalThis.location?.hostname || '').toLowerCase();
  const base = (BASE_DOMAIN || '').toLowerCase();
  if (!base || (host !== base && !host.endsWith(`.${base}`))) return '';
  return `; Domain=.${base}`;
}

function writeCookie(value, extra) {
  if (typeof document === 'undefined') return;
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; SameSite=Lax${domainAttribute()}${secure}${extra}`;
}

/** Records that the user logged out of the portal. */
export function markLoggedOut() {
  writeCookie(MARKED_VALUE, '');
}

/** Removes the marker after a completed sign-in. */
export function clearLoggedOutMark() {
  writeCookie('', '; Max-Age=0');
}

/** True while the user is logged out of the portal but the shared Keycloak session may still exist. */
export function isLoggedOutMarked() {
  if (typeof document === 'undefined' || !document.cookie) return false;
  return document.cookie.split('; ').some((entry) => entry === `${COOKIE_NAME}=${MARKED_VALUE}`);
}

/**
 * Keycloak login options with `prompt: 'login'` added while the marker is set, so Keycloak shows
 * its sign-in form instead of reusing the session NumberCruncher Web is still using. Returns the
 * options unchanged (same object, possibly `undefined`) when the marker is not set.
 * @param {object} [options] keycloak-js login / createLoginUrl options
 */
export function withLoginPrompt(options) {
  if (!isLoggedOutMarked()) return options;
  return { ...options, prompt: 'login' };
}
